/**
 * ImportClientesPage Component
 * 
 * Página para importação de clientes via arquivo XML.
 * 
 * Funcionalidades:
 * - Upload de arquivo XML
 * - Validação de arquivo
 * - Processamento e importação
 * - Exibição de relatório detalhado
 */

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, FileText, CheckCircle2, XCircle, 
  AlertCircle, Loader2, ArrowLeft
} from 'lucide-react';
import { customerService } from '../../../../services/erpApi';
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal';
import './ImportClientesPage.css';

interface ImportPreview {
  total: number;
  valid: number;
  invalid: number;
  customers: Array<{
    codigo: string;
    nome: string;
    cpf_cnpj: string | null;
    tipo_pessoa: string | null;
    telefone: string | null;
    email: string | null;
    valid: boolean;
    errors: string[];
  }>;
}

interface ImportReport {
  total: number;
  created: number;
  updated: number;
  errors: number;
  usersCreated: number;
  details: Array<{
    code: string;
    status: 'success' | 'error';
    customerId: number | null;
    errors: string[];
    userCreated: boolean;
  }>;
}

const ImportClientesPage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleFileSelect = (file: File) => {
    // Validar tipo de arquivo
    const isExcel = file.name.toLowerCase().endsWith('.xlsx') || 
                   file.name.toLowerCase().endsWith('.xls') ||
                   file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                   file.type === 'application/vnd.ms-excel';
    
    if (!isExcel) {
      setError('Apenas arquivos Excel (.xlsx, .xls) são permitidos');
      return;
    }

    // Validar tamanho (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('Arquivo muito grande. Tamanho máximo: 10MB');
      return;
    }

    setError(null);
    setSelectedFile(file);
    setPreview(null);
    setReport(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreview(null);
    setReport(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setError(null);
    setPreview(null);
    setReport(null);
    setShowConfirmModal(false); // Garantir que modal não está aberto

    try {
      // Primeiro fazer preview (apenas extração e validação, SEM salvar)
      const previewResponse = await customerService.previewImport(selectedFile);

      if (previewResponse.success) {
        setPreview(previewResponse.data);
        // NÃO abrir modal automaticamente - usuário deve revisar e clicar em "Confirmar Importação"
      } else {
        setError(previewResponse.message || 'Erro ao processar arquivo');
      }
    } catch (err: any) {
      console.error('Erro ao fazer preview:', err);
      setError(err.response?.data?.message || 'Erro ao processar arquivo. Verifique o arquivo e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!selectedFile) return;

    setShowConfirmModal(false);
    setLoading(true);
    setError(null);

    try {
      // Agora fazer a importação real
      const response = await customerService.importFromXml(selectedFile);

      if (response.success) {
        setReport(response.data);
        setPreview(null); // Limpar preview após importação
      } else {
        setError(response.message || 'Erro ao importar clientes');
      }
    } catch (err: any) {
      console.error('Erro ao importar:', err);
      setError(err.response?.data?.message || 'Erro ao importar clientes. Verifique o arquivo e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="import-clientes-page">
      <div className="import-header">
        <button 
          onClick={() => navigate('/erp/pessoas/clientes')}
          className="import-back-btn"
        >
          <ArrowLeft size={20} />
          Voltar
        </button>
        <div>
          <h1>Importar Clientes</h1>
          <p>Importe uma lista de clientes a partir de arquivo Excel</p>
        </div>
      </div>

      {error && !loading && (
        <div className="import-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      <div className="import-content">
        {/* Área de Upload */}
        <div className="import-upload-section">
          <h2>Selecionar Arquivo Excel</h2>
          
          {!selectedFile ? (
            <div
              className={`import-dropzone ${isDragging ? 'dragging' : ''} ${error ? 'error' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              
              <div className="import-dropzone-content">
                <Upload size={48} className="import-upload-icon" />
                <p className="import-dropzone-text">
                  <strong>Arraste e solte</strong> um arquivo Excel aqui ou <strong>clique para selecionar</strong>
                </p>
                <p className="import-dropzone-hint">
                  Apenas arquivos Excel (.xlsx, .xls) • Tamanho máximo: 10MB
                </p>
              </div>
            </div>
          ) : (
            <div className="import-file-selected">
              <div className="import-file-info">
                <FileText size={24} />
                <div>
                  <p className="import-file-name">{selectedFile.name}</p>
                  <p className="import-file-size">{formatFileSize(selectedFile.size)}</p>
                </div>
              </div>
              <button 
                onClick={handleRemoveFile}
                className="import-remove-btn"
                disabled={loading}
              >
                <XCircle size={20} />
              </button>
            </div>
          )}

          {selectedFile && !preview && !report && (
            <button
              onClick={handleImport}
              className="import-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Processando...
                </>
              ) : (
                <>
                  <Upload size={20} />
                  Analisar Arquivo
                </>
              )}
            </button>
          )}
        </div>

        {/* Preview da Importação */}
        {preview && !report && (
          <div className="import-preview-section">
            <h2>Preview da Importação</h2>
            <p className="import-preview-description">
              Revise os dados extraídos antes de confirmar a importação. Apenas os registros válidos serão importados.
            </p>
            
            <div className="import-preview-summary">
              <div className="import-preview-summary-card">
                <div className="import-preview-summary-icon total">
                  <FileText size={24} />
                </div>
                <div>
                  <p className="import-preview-summary-value">{preview.total}</p>
                  <p className="import-preview-summary-label">Total de Registros</p>
                </div>
              </div>

              <div className="import-preview-summary-card success">
                <div className="import-preview-summary-icon success">
                  <CheckCircle2 size={24} />
                </div>
                <div>
                  <p className="import-preview-summary-value">{preview.valid}</p>
                  <p className="import-preview-summary-label">Válidos</p>
                </div>
              </div>

              <div className="import-preview-summary-card error">
                <div className="import-preview-summary-icon error">
                  <XCircle size={24} />
                </div>
                <div>
                  <p className="import-preview-summary-value">{preview.invalid}</p>
                  <p className="import-preview-summary-label">Inválidos</p>
                </div>
              </div>
            </div>

            {/* Lista de Clientes Preview */}
            {preview.customers.length > 0 && (
              <div className="import-preview-list">
                <h3>Clientes Extraídos</h3>
                <div className="import-preview-table-wrapper">
                  <table className="import-preview-table">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Nome</th>
                        <th>CPF/CNPJ</th>
                        <th>Tipo</th>
                        <th>Telefone</th>
                        <th>E-mail</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.customers.slice(0, 20).map((customer, index) => (
                        <tr 
                          key={index}
                          className={customer.valid ? 'valid' : 'invalid'}
                        >
                          <td>{customer.codigo}</td>
                          <td>{customer.nome}</td>
                          <td>{customer.cpf_cnpj || '-'}</td>
                          <td>{customer.tipo_pessoa || '-'}</td>
                          <td>{customer.telefone || '-'}</td>
                          <td>{customer.email || '-'}</td>
                          <td>
                            {customer.valid ? (
                              <span className="preview-status-valid">
                                <CheckCircle2 size={16} />
                                Válido
                              </span>
                            ) : (
                              <span className="preview-status-invalid">
                                <XCircle size={16} />
                                Inválido
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.customers.length > 20 && (
                    <p className="import-preview-more">
                      ... e mais {preview.customers.length - 20} registro(s)
                    </p>
                  )}
                </div>

                {/* Erros dos registros inválidos */}
                {preview.customers.some(c => !c.valid) && (
                  <div className="import-preview-errors">
                    <h4>Erros Encontrados</h4>
                    {preview.customers
                      .filter(c => !c.valid)
                      .slice(0, 10)
                      .map((customer, index) => (
                        <div key={index} className="import-preview-error-item">
                          <strong>{customer.codigo} - {customer.nome}:</strong>
                          <ul>
                            {customer.errors.map((err, errIndex) => (
                              <li key={errIndex}>{err}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            <div className="import-preview-actions">
              <button
                onClick={handleRemoveFile}
                className="import-preview-cancel-btn"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                onClick={() => setShowConfirmModal(true)}
                className="import-preview-confirm-btn"
                disabled={loading || preview.valid === 0}
              >
                {preview.valid > 0 ? `Confirmar Importação (${preview.valid} válidos)` : 'Nenhum registro válido'}
              </button>
            </div>
          </div>
        )}

        {/* Relatório de Importação */}
        {report && (
          <div className="import-report-section">
            <h2>Relatório de Importação</h2>
            
            <div className="import-summary">
              <div className="import-summary-card">
                <div className="import-summary-icon total">
                  <FileText size={24} />
                </div>
                <div>
                  <p className="import-summary-value">{report.total}</p>
                  <p className="import-summary-label">Total Processado</p>
                </div>
              </div>

              <div className="import-summary-card success">
                <div className="import-summary-icon success">
                  <CheckCircle2 size={24} />
                </div>
                <div>
                  <p className="import-summary-value">{report.created}</p>
                  <p className="import-summary-label">Criados</p>
                </div>
              </div>

              <div className="import-summary-card updated">
                <div className="import-summary-icon updated">
                  <CheckCircle2 size={24} />
                </div>
                <div>
                  <p className="import-summary-value">{report.updated}</p>
                  <p className="import-summary-label">Atualizados</p>
                </div>
              </div>

              <div className="import-summary-card error">
                <div className="import-summary-icon error">
                  <XCircle size={24} />
                </div>
                <div>
                  <p className="import-summary-value">{report.errors}</p>
                  <p className="import-summary-label">Erros</p>
                </div>
              </div>

              <div className="import-summary-card users">
                <div className="import-summary-icon users">
                  <CheckCircle2 size={24} />
                </div>
                <div>
                  <p className="import-summary-value">{report.usersCreated}</p>
                  <p className="import-summary-label">Usuários Criados</p>
                </div>
              </div>
            </div>

            {/* Detalhes */}
            {report.details.length > 0 && (
              <div className="import-details">
                <h3>Detalhes da Importação</h3>
                <div className="import-details-list">
                  {report.details.map((detail, index) => (
                    <div 
                      key={index} 
                      className={`import-detail-item ${detail.status === 'error' ? 'error' : 'success'}`}
                    >
                      <div className="import-detail-main">
                        {detail.status === 'success' ? (
                          <CheckCircle2 size={18} className="import-detail-icon success" />
                        ) : (
                          <XCircle size={18} className="import-detail-icon error" />
                        )}
                        <div>
                          <p className="import-detail-code">
                            Código: <strong>{detail.code}</strong>
                          </p>
                          {detail.customerId && (
                            <p className="import-detail-id">ID: {detail.customerId}</p>
                          )}
                          {detail.userCreated && (
                            <p className="import-detail-user">✓ Conta de usuário criada</p>
                          )}
                        </div>
                      </div>
                      {detail.errors.length > 0 && (
                        <div className="import-detail-errors">
                          {detail.errors.map((err, errIndex) => (
                            <p key={errIndex} className="import-detail-error">
                              {err}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="import-actions">
              <button
                onClick={() => navigate('/erp/pessoas/clientes')}
                className="import-view-btn"
              >
                Ver Clientes
              </button>
              <button
                onClick={handleRemoveFile}
                className="import-new-btn"
              >
                Nova Importação
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Confirmação */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmImport}
        type="warning"
        title="Confirmar Importação"
        message={
          <div>
            <p>
              Você está prestes a importar <strong>{preview?.valid || 0} cliente(s) válido(s)</strong> do arquivo <strong>{selectedFile?.name}</strong>.
            </p>
            {preview && preview.invalid > 0 && (
              <p style={{ marginTop: '0.75rem', color: '#dc2626' }}>
                <strong>Atenção:</strong> {preview.invalid} registro(s) inválido(s) serão ignorados.
              </p>
            )}
            <p style={{ marginTop: '0.75rem' }}>
              Esta ação irá:
            </p>
            <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem', color: '#374151' }}>
              <li>Criar novos clientes que não existem no sistema</li>
              <li>Atualizar clientes existentes com base no código</li>
              <li>Criar contas de usuário para clientes com e-mail válido</li>
            </ul>
            <p style={{ marginTop: '0.75rem', fontWeight: 600, color: '#dc2626' }}>
              Esta ação não pode ser desfeita. Deseja continuar?
            </p>
          </div>
        }
        confirmText="Sim, Importar"
        cancelText="Cancelar"
        icon={undefined}
      />
    </div>
  );
};

export default ImportClientesPage;
